import React, { useEffect, useState } from "react";
import {
  Search,
  Heart,
  ShoppingCart,
  User,
  Settings,
  Package,
  HeartIcon,
  Star,
  LogOut,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import PersonalInfo from "./PersonalInfo";
import MyOrders from "./MyOrders";
import Wishlist from "./Wishlist";
import Reviews from "./Reviews";
import Footer from "../../components/Footer";
import Header from "../../components/Header";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../../firebase";
import { onAuthStateChanged } from "firebase/auth";

const ProfilePage = () => {
  const [activeSection, setActiveSection] = useState("personal-info");
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  const menuItems = [
    { id: "personal-info", label: "Personal Information", icon: User },
    { id: "my-orders", label: "My Orders", icon: Package },
    { id: "wishlist", label: "Wishlist", icon: Heart },
    { id: "reviews", label: "My Reviews", icon: Star },
  ];

  const renderSection = () => {
    switch (activeSection) {
      case "personal-info":
        return <PersonalInfo />;
      case "my-orders":
        return <MyOrders />;
      case "wishlist":
        return <Wishlist />;
      case "reviews":
        return <Reviews />;
      default:
        return <PersonalInfo />;
    }
  };
  useEffect(() => {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }, []);
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      const fetchUserData = async () => {
        if (!currentUser) {
          // User is not logged in
          // You might want to redirect to login page or handle this case
          return;
        }
  
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userDoc = await getDoc(userDocRef);
  
          if (userDoc.exists()) {
            const userData = userDoc.data();
            const formattedData = {
              username: userData.username || "",
              email: userData.email || currentUser.email || "",
              phone: userData.phone || "",
              dob: userData.dob || "",
              createdAt: userData.createdAt || "",
              lastUpdated: userData.lastUpdated || "",
              isVerified: userData.isVerified || false,
              accountType: userData.accountType || "email",
              profileComplete: userData.profileComplete || false,
            };
            setUser(formattedData);
          } else {
            const initialData = {
              username: currentUser.displayName || "",
              email: currentUser.email || "",
              phone: "",
              dob: "",
              createdAt: new Date().toISOString(),
              lastUpdated: new Date().toISOString(),
              isVerified: false,
              accountType: "email",
              profileComplete: false,
            };
            setUser(initialData);
          }
        } catch (err) {
          console.error("Error fetching user data:", err);
          // setError("Failed to load user information. Please try again later.");
        } finally {
          // setLoading(false);
        }
      };
  
      fetchUserData();
    });
  
    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, []);
  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-purple-50 via-white to-purple-50">
      <Header />

      {/* Profile Content */}
      <div className="container mx-auto px-6 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl shadow-md p-6 transition-all duration-300 hover:shadow-xl">
              <div className="flex items-center space-x-4 mb-8">
                <div className="w-16 h-16 rounded-full bg-purple-100 flex items-center justify-center">
                  <User className="w-8 h-8 text-purple-700" />
                </div>
                <div>
                  <h3 className="font-medium text-purple-900">
                    {user?.username}
                  </h3>
                  <p className="text-sm text-purple-600">{user?.email}</p>
                </div>
              </div>

              <nav className="space-y-2">
                {menuItems.map((item) => {
                  const Icon = item.icon;
                  return (
                    <button
                      key={item.id}
                      onClick={() => setActiveSection(item.id)}
                      className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all duration-200 transform hover:scale-102 ${
                        activeSection === item.id
                          ? "bg-purple-700 text-white shadow-md"
                          : "text-purple-700 hover:bg-purple-50"
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </button>
                  );
                })}

                <button className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-500 hover:bg-red-50 transition-all duration-200 transform hover:scale-102">
                  <LogOut className="w-5 h-5" />
                  <span>Logout</span>
                </button>
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            <div className="bg-white rounded-xl shadow-md p-6 min-h-[600px] transition-all duration-300 hover:shadow-xl">
              <div className="relative">
                {/* Decorative pattern overlay */}
                <div
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    backgroundImage: `
                         linear-gradient(to right, rgba(147, 51, 234, 0.05) 1px, transparent 1px),
                         linear-gradient(to bottom, rgba(147, 51, 234, 0.05) 1px, transparent 1px)
                       `,
                    backgroundSize: "50px 50px",
                    opacity: 0.5,
                  }}
                />
                {/* Content */}
                <div className="relative z-10">{renderSection()}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProfilePage;
